import axios from "axios";

/**
 * The chart-preference store behind `useChartPreference` (./preferences.ts), kept free of React,
 * Next and the chart library so it can be unit-tested (e2e/unit/chartPreferences.spec.ts).
 *
 * Three layers, read in this order: memory, localStorage, the platform
 * (GET /interface/api/user/chart-preferences/, which also carries the
 * staff-set defaults). A save writes memory and localStorage at once and
 * posts to the platform after a short debounce, so a burst of toggles is one
 * request. Signed-out users keep localStorage only, and nothing is sent to the
 * platform for them: it answers a visitor with 403, which every signed-out
 * stock page used to log as a console error (ortex-gui#1364).
 */
export type PreferenceDoc = Record<string, unknown> & { updatedAt?: number };

export interface PreferenceState {
    preferences: Record<string, PreferenceDoc>;
    defaults: Record<string, PreferenceDoc>;
    loaded: boolean;
}

export const PREFERENCES_URL = "/interface/api/user/chart-preferences/";
const LOCAL_KEY = "ortex-charts:preferences:v1";
const POST_DEBOUNCE_MS = 800;

const state: PreferenceState = { preferences: {}, defaults: {}, loaded: false };
const listeners = new Set<() => void>();
let loadPromise: Promise<void> | null = null;
let pending: Record<string, PreferenceDoc | null> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let localRead = false;
/** Whether the platform copy is in play: set by the first signed-in load. */
let remote = false;

function emit(): void {
    listeners.forEach((l) => l());
}

function readLocal(): Record<string, PreferenceDoc> {
    try {
        const raw = typeof window !== "undefined" ? window.localStorage.getItem(LOCAL_KEY) : null;
        return raw ? (JSON.parse(raw) as Record<string, PreferenceDoc>) : {};
    } catch {
        return {};
    }
}

function writeLocal(): void {
    try {
        window.localStorage.setItem(LOCAL_KEY, JSON.stringify(state.preferences));
    } catch {
        // Quota or private mode: the platform copy is the durable one.
    }
}

/** Newer document wins; a document without a timestamp counts as oldest. */
function newer(a: PreferenceDoc | undefined, b: PreferenceDoc | undefined): PreferenceDoc | undefined {
    if (!a) return b;
    if (!b) return a;
    return (b.updatedAt ?? 0) > (a.updatedAt ?? 0) ? b : a;
}

/**
 * Read localStorage into memory, once per page life. Runs on mount, before the account is known,
 * and before any save, so a save can never overwrite the stored documents with only itself.
 */
export function loadLocalPreferences(): void {
    if (typeof window === "undefined" || localRead) return;
    localRead = true;
    // Anything saved in memory before this ran is newer than the stored copy.
    state.preferences = { ...readLocal(), ...state.preferences };
    emit();
}

/**
 * Load once per page life: localStorage first (instant), then the platform, keeping whichever
 * copy is newer per key. Signed out, the platform is skipped; a later signed-in call still
 * fetches it and pushes up anything saved locally in the meantime.
 */
export function loadPreferences(signedIn = true): Promise<void> {
    if (typeof window === "undefined") return Promise.resolve();
    if (loadPromise) return loadPromise;
    loadLocalPreferences();
    if (!signedIn) {
        if (!state.loaded) {
            state.loaded = true;
            emit();
        }
        return Promise.resolve();
    }
    remote = true;
    loadPromise = axios
        .get<{ preferences: Record<string, PreferenceDoc>; defaults: Record<string, PreferenceDoc> }>(PREFERENCES_URL)
        .then(({ status, data }) => {
            if (status !== 200 || !data || typeof data !== "object") return;
            const merged: Record<string, PreferenceDoc> = { ...state.preferences };
            for (const [key, doc] of Object.entries(data.preferences ?? {})) merged[key] = newer(merged[key], doc) as PreferenceDoc;
            // Anything local and newer than the server copy is pushed back.
            for (const [key, doc] of Object.entries(state.preferences)) {
                const server = data.preferences?.[key];
                if (!server || (doc.updatedAt ?? 0) > (server.updatedAt ?? 0)) pending[key] = doc;
            }
            state.preferences = merged;
            state.defaults = data.defaults ?? {};
            writeLocal();
            // Also carries saves made while signed out, or before this load finished.
            if (Object.keys(pending).length) scheduleFlush();
        })
        .catch(() => {
            // Offline, or the session is gone: localStorage carries the user through.
        })
        .finally(() => {
            state.loaded = true;
            emit();
        });
    return loadPromise;
}

function scheduleFlush(): void {
    // Signed out: pending saves wait in memory (and localStorage) for a signed-in load.
    if (!remote) return;
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, POST_DEBOUNCE_MS);
}

async function flush(): Promise<void> {
    flushTimer = null;
    const batch = pending;
    pending = {};
    if (!Object.keys(batch).length) return;
    try {
        await axios.post(PREFERENCES_URL, { preferences: batch });
    } catch {
        // Next save retries the lot; localStorage already has it.
        pending = { ...batch, ...pending };
    }
}

export function getPreference(key: string): PreferenceDoc | undefined {
    return state.preferences[key];
}

/** Every saved document whose key starts with `prefix`, e.g. the named templates. */
export function listPreferences(prefix: string): Array<[string, PreferenceDoc]> {
    return Object.entries(state.preferences).filter(([key, doc]) => key.startsWith(prefix) && doc);
}

export function getDefault(key: string): PreferenceDoc | undefined {
    return state.defaults[key];
}

/** Whether the load for this page has finished (localStorage only when signed out). */
export function preferencesLoaded(): boolean {
    return state.loaded;
}

/** Save (or with `null`, delete) a document: memory and localStorage now, the platform shortly. */
export function savePreference(key: string, doc: PreferenceDoc | null): void {
    loadLocalPreferences();
    if (doc === null) delete state.preferences[key];
    else state.preferences[key] = { ...doc, updatedAt: Date.now() };
    writeLocal();
    pending[key] = doc === null ? null : state.preferences[key];
    scheduleFlush();
    emit();
}

export function subscribePreferences(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Flush a pending save before the page goes away. */
if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        if (!remote) return;
        const batch = pending;
        pending = {};
        if (!Object.keys(batch).length) return;
        try {
            const blob = new Blob([JSON.stringify({ preferences: batch })], { type: "application/json" });
            // Cookies travel with sendBeacon on the same origin; the CSRF header does not, so this
            // is best effort and the next page load re-posts anything still newer locally.
            navigator.sendBeacon?.((axios.defaults.baseURL ?? "") + PREFERENCES_URL, blob);
        } catch {
            pending = batch;
        }
    });
}
